/**
 * @file The action service
 * @author Heino Lenting
 */
import * as tslib_1 from "tslib";
import { FileReaderService } from '../base/file-reader.service';
import { UploadImportTasksAction } from './action/actions/upload-import-tasks.action';
import { UploadImportUsersAction } from './action/actions/upload-import-users.action';
import { RealizeSelectionAction } from './action/actions/realize-selection.action';
import { ChangeMarketplaceValueAction } from './action/actions/change-marketplace-value.action';
import { ChangeWorkStatusValueAction } from './action/actions/change-workstatus-value.action';
import { UserService } from '../user.service';
import { BackendSettingsService } from '../backend-settings.service';
import { OpenOverlayAction } from './action/actions/open-overlay.action';
import { OverlayService } from './overlay.service';
import { AlertsService } from './alerts.service';
import { CanCloneAction } from './action/actions/can-clone-action';
import { TranslateService } from '@ngx-translate/core';
import { OpenOverlayShrinkageAction } from './action/actions/open-overlay-shrinkage.action';
import { CancelPlanMacroAction } from './action/actions/cancel-planmacro.action';
import { DomainService } from '../domain.service';
import { EventsService } from './event.service';
import { DoMethodAction } from './action/actions/do-method-action';
import { EnableUserSelectionAction } from './action/actions/enable-user-selection.action';
var ActionService = /** @class */ (function () {
    /**
     * Creates an instance of ActionService.
     * @param {FileReaderService} fileReaderService
     * @memberof ActionService
     */
    function ActionService(fileReaderService, userService, backendSettingsService, overlayService, alertsService, translateService, domainService, eventsService) {
        this.fileReaderService = fileReaderService;
        this.userService = userService;
        this.backendSettingsService = backendSettingsService;
        this.overlayService = overlayService;
        this.alertsService = alertsService;
        this.translateService = translateService;
        this.domainService = domainService;
        this.eventsService = eventsService;
        this.availableActions = {
            'upload-import-tasks': new UploadImportTasksAction(this.fileReaderService),
            'upload-import-users': new UploadImportUsersAction(this.fileReaderService),
            'realize-list': new RealizeSelectionAction(this.userService),
            'turn-marketplace-on': new ChangeMarketplaceValueAction(this.userService, this.backendSettingsService, true, 'APP.LISTS.MARKETPLACE_ON'),
            'turn-marketplace-off': new ChangeMarketplaceValueAction(this.userService, this.backendSettingsService, false, 'APP.LISTS.MARKETPLACE_OFF'),
            'open-overlay-exchange': new OpenOverlayAction(this.overlayService, 'sym-icon-exchange', 'APP.SOCIAL.ACTIONS_EXCHANGE'),
            'open-overlay-reassign': new OpenOverlayAction(this.overlayService, 'sym-icon-reassign', 'APP.SOCIAL.ACTIONS_REASSIGN'),
            'open-overlay-exchange-day': new OpenOverlayAction(this.overlayService, 'sym-icon-exchange', 'APP.SOCIAL.ACTIONS_EXCHANGE_DAY'),
            'open-overlay-reassign-day': new OpenOverlayAction(this.overlayService, 'sym-icon-reassign', 'APP.SOCIAL.ACTIONS_REASSIGN_DAY'),
            'open-overlay-undo': new OpenOverlayAction(this.overlayService, 'sym-icon-undo', 'APP.MARKETPLACE.WITHDRAW'),
            'open-overlay-agenda': new OpenOverlayAction(this.overlayService, 'sym-icon-agenda', 'APP.FORECAST.CHECK_CALENDAR'),
            'open-overlay-workslip': new OpenOverlayAction(this.overlayService, 'sym-icon-workslip', 'APP.WORKSLIP.WORKSLIP'),
            'open-overlay-shrinkage': new OpenOverlayShrinkageAction(this.overlayService),
            'open-overlay-clone': new OpenOverlayAction(this.overlayService, 'sym-icon-clone', 'APP.TEMPLATES.DUPLICATE'),
            'open-overlay-demand': new OpenOverlayAction(this.overlayService, 'sym-icon-pick', 'APP.LISTS.COLUMN.MARKETPLACE-DEMAND.ATTEND'),
            'open-overlay-service': new OpenOverlayAction(this.overlayService, 'sym-icon-pick', 'APP.LISTS.COLUMN.MARKETPLACE-SERVICE.ATTEND'),
            'can-clone': new CanCloneAction(this.alertsService, this.translateService, this.overlayService, this.domainService, this.eventsService),
            'batch-planmacro': new OpenOverlayAction(this.overlayService, 'sym-icon-batch', 'APP.MISC.PLANMACRO.BATCH'),
            'split-availability': new OpenOverlayAction(this.overlayService, 'sym-icon-split', 'APP.MISC.REPEAT.SPLIT'),
            'cancel-planmacro': new CancelPlanMacroAction(this.domainService),
            'workstatus-approve': new ChangeWorkStatusValueAction('APPROVED', 'sym-icon-approve', 'APP.LISTS.APPROVE'),
            'workstatus-decline': new ChangeWorkStatusValueAction('DECLINED', 'sym-icon-decline', 'APP.LISTS.DECLINE'),
            'enable-user': new DoMethodAction(this.domainService, this.eventsService, 'sym-icon-enable', 'APP.MISC.USER.ENABLE'),
            'disable-user': new DoMethodAction(this.domainService, this.eventsService, 'sym-icon-disable', 'APP.MISC.USER.DISABLE'),
            'enable-user-selection': new EnableUserSelectionAction()
        };
    }
    /**
     * Used to load the action based on the name
     * @param {string} name The name of action
     * @returns {Promise<ActionBase>} The action
     * @memberof ActionService
     */
    ActionService.prototype.getAction = function (name) {
        return this.availableActions[name];
    };
    /**
     * Exectue the action
     * @param {string} name The name of the action
     * @param {ActionConfiguration} actionConfiguration The configuration
     * @returns {Promise<string>} The success message
     */
    ActionService.prototype.doAction = function (input) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var config, name, action;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        config = input.actionConfiguration;
                        name = config.name;
                        if (this.domainService.hasChanges()) {
                            return [2 /*return*/, Promise.reject({ labelKey: 'APP.SOCIAL.SAVE_CHANGES_FIRST' })];
                        }
                        if (!config) {
                            return [2 /*return*/, Promise.reject({ labelKey: 'APP.CORE.CONFIGURATION_MISSING' })];
                        }
                        if (!this.availableActions[name]) {
                            return [2 /*return*/, Promise.reject({ labelKey: 'APP.SOCIAL.ACTION_NOT_FOUND', message: 'Action does not exists (' + name + ')' })];
                        }
                        action = this.getAction(name);
                        return [4 /*yield*/, action.do(input)];
                    case 1: return [2 /*return*/, _a.sent()];
                }
            });
        });
    };
    /**
     * Returns whether the action is allowed
     * @param {ActionConfiguration} action The return value is based on the action name.
     * @param {Entity} entity The entity in question.
     * @returns {boolean} A value indicating whether the action should be allowed.
     * @memberOf ActionService
     */
    ActionService.prototype.isAllowed = function (action, entity) {
        if (!this.userService.isActionAllowed(action) || !action.name) {
            return false;
        }
        return this.getAction(action.name).isAllowed(entity, action.name);
    };
    /**
     * Returns whether the action is disabled.
     * @param {ActionConfiguration} action The return value is based on the action name.
     * @param {Entity} item The entity in question.
     * @returns {boolean} A value indicating whether the action should be disabled.
     * @memberOf ActionService
     */
    ActionService.prototype.isDisabled = function (action, item) {
        if (!item || !action.name) {
            return true;
        }
        return this.getAction(action.name).isDisabled(item, action.name, this.backendSettingsService, this.userService);
    };
    /**
     * Returns the icon belonging to the action.
     * @param {ActionConfiguration} action The icon is chosen from the action name.
     * @returns {string} An icon class name.
     * @memberOf ActionService
     */
    ActionService.prototype.getIcon = function (action) {
        if (!action.name) {
            return action.icon || 'sym-icon-actions';
        }
        return this.getAction(action.name).getIcon();
    };
    /**
     * Returns the label key belonging the action.
     * @param {ActionConfiguration} action The label is chosen from the action name.
     * @returns {string} A translation label.
     * @memberOf ActionService
     */
    ActionService.prototype.getLabelKey = function (action) {
        if (!action.name) {
            return action.labelKey || 'APP.SETTINGS.UNKNOWN_ACTION';
        }
        return this.getAction(action.name).getLabelKey();
    };
    return ActionService;
}());
export { ActionService };
